import { useState } from 'react';
import styled from 'styled-components';
import type { KsPackageAct, KsPackageIssue, KsPackageLine } from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Badge } from '@shared/ui/Badge';
import { Input, Select, Label, Field } from '@shared/ui/Input';
import { useMatchKsPackageLineMutation } from '@shared/api';
import {
  fmtMoney,
  fmtNum,
  matchLabel,
  matchTone,
} from '@shared/lib/smetaLabels';

const TableTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-top: ${({ theme }) => theme.spacing(5)};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-wrap: wrap;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    text-align: left;
    padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(2.5)};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    vertical-align: top;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => theme.colors.text.muted};
    white-space: nowrap;
  }

  td.num {
    text-align: right;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }

  tr[data-severity='warning'] td {
    background: rgba(245, 124, 0, 0.12);
  }

  tr[data-severity='error'] td {
    background: rgba(220, 50, 50, 0.12);
  }

  tr[data-selected='true'] td {
    background: ${({ theme }) => theme.colors.primarySoft};
  }
`;

const ThRight = styled.th`
  text-align: right !important;
`;

const Inline = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const AmbiguousMatchInline = styled(Inline)`
  margin-top: ${({ theme }) => theme.spacing(1.5)};
`;

const FilterSelect = styled(Select)`
  max-width: 220px;
`;

const BulkBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => theme.spacing(3)};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
`;

const ReasonField = styled(Field)`
  margin-bottom: 0;
  flex: 1;
  min-width: 240px;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Muted = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const NoLinesMuted = styled(Muted)`
  margin-top: ${({ theme }) => theme.spacing(2)};
`;

function worstSeverity(issues: KsPackageIssue[]): 'error' | 'warning' | 'info' | null {
  if (issues.length === 0) return null;
  if (issues.some((i) => i.severity === 'error')) return 'error';
  if (issues.some((i) => i.severity === 'warning')) return 'warning';
  return 'info';
}

function apiErr(err: unknown, fallback: string): string {
  const m = (err as { data?: { error?: { message?: string } } })?.data?.error?.message;
  return typeof m === 'string' && m.trim() ? m : fallback;
}

function LineRow({
  line,
  issues,
  packageId,
  canWrite,
  frozen,
  selected,
  onToggle,
  onNotice,
}: {
  line: KsPackageLine;
  issues: KsPackageIssue[];
  packageId: string;
  canWrite: boolean;
  frozen: boolean;
  selected: boolean;
  onToggle: (id: string) => void;
  onNotice: (text: string) => void;
}) {
  const [candidate, setCandidate] = useState('');
  const [matchLine, { isLoading }] = useMatchKsPackageLineMutation();
  const severity = worstSeverity(issues);
  const candidates = Array.isArray(line.match_candidates) ? line.match_candidates : [];

  async function handleMatch() {
    if (!candidate) return;
    try {
      await matchLine({ packageId, lineId: line.id, estimate_item_id: candidate }).unwrap();
      onNotice(
        'Ручной матч записан в правки. В строке он появится после пересборки — ' +
          'нажмите «Собрать».',
      );
      setCandidate('');
    } catch (err) {
      onNotice(apiErr(err, 'Не удалось записать ручной матч.'));
    }
  }

  return (
    <tr data-severity={severity ?? undefined} data-selected={selected ? 'true' : undefined}>
      <td>
        <input
          type="checkbox"
          checked={selected}
          disabled={frozen}
          onChange={() => onToggle(line.id)}
          aria-label={`Выбрать строку ${line.line_no ?? line.name}`}
          title={frozen ? 'Пакет выпущен — состав заморожен' : undefined}
        />
      </td>
      <td className="num">{line.line_no ?? '—'}</td>
      <td>{line.code ?? '—'}</td>
      <td>
        {line.name}
        {line.name_note && <Muted>{line.name_note}</Muted>}
        {line.excluded_from_total && <Muted>исключена из итога</Muted>}
        {issues.length > 0 && <Muted>{issues.map((i) => i.message).join(' · ')}</Muted>}
      </td>
      <td>{line.unit ?? '—'}</td>
      <td className="num">{fmtNum(line.qty)}</td>
      <td className="num">
        {fmtNum(line.contract_qty)}
        {line.over_contract ? ' ⚠' : ''}
      </td>
      <td className="num">{fmtMoney(line.unit_price)}</td>
      <td className="num">{fmtMoney(line.cost_total)}</td>
      <td>
        <Badge $tone={matchTone(line.match_status)}>{matchLabel(line.match_status)}</Badge>
        {line.match_status === 'ambiguous' && candidates.length > 0 && (
          <AmbiguousMatchInline>
            <Select
              value={candidate}
              onChange={(e) => setCandidate(e.target.value)}
              disabled={!canWrite || frozen}
            >
              <option value="">— выбрать позицию сметы —</option>
              {candidates.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} · {c.name.slice(0, 60)}
                  {c.unit ? ` · ${c.unit}` : ''}
                </option>
              ))}
            </Select>
            <Button
              $variant="secondary"
              $size="sm"
              type="button"
              disabled={!candidate || isLoading || !canWrite || frozen}
              title={!canWrite ? 'Только чтение' : undefined}
              onClick={handleMatch}
            >
              Матч
            </Button>
          </AmbiguousMatchInline>
        )}
      </td>
    </tr>
  );
}

export interface KsPackageLinesTabProps {
  packageId: string;
  lines: KsPackageLine[];
  acts: KsPackageAct[];
  actFilter: string;
  onActFilterChange: (id: string) => void;
  issuesByLine: Map<string, KsPackageIssue[]>;
  canWrite: boolean;
  frozen: boolean;
  onNotice: (msg: string) => void;
  onBulkExclude: (selected: Set<string>, exclude: boolean, reason: string) => Promise<void>;
  onBulkDecision: (selected: Set<string>, decision: 'accepted', subset: 'safe' | 'errors') => Promise<void>;
}

export function KsPackageLinesTab({
  packageId,
  lines,
  acts,
  actFilter,
  onActFilterChange,
  issuesByLine,
  canWrite,
  frozen,
  onNotice,
  onBulkExclude,
  onBulkDecision,
}: KsPackageLinesTabProps) {
  const [selectedLines, setSelectedLines] = useState<Set<string>>(new Set());
  const [bulkReason, setBulkReason] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const ambiguousCount = lines.filter((l) => l.match_status === 'ambiguous').length;

  const toggleLine = (id: string) => {
    setSelectedLines((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllLines = () => {
    if (selectedLines.size === lines.length) {
      setSelectedLines(new Set());
    } else {
      setSelectedLines(new Set(lines.map((l) => l.id)));
    }
  };

  const selectedIssues = Array.from(selectedLines).flatMap(
    (id) => issuesByLine.get(id) ?? [],
  );
  const pendingIssues = selectedIssues.filter((i) => i.decision === 'pending');
  const bulkPending = {
    safe: pendingIssues.filter((i) => i.severity !== 'error'),
    errors: pendingIssues.filter((i) => i.severity === 'error'),
  };

  const bulkSafeTitle = !canWrite
    ? 'Только чтение'
    : frozen
      ? 'Пакет выпущен — решения по находкам заморожены'
      : bulkPending.safe.length === 0
        ? 'Нет неразобранных предупреждений у выбранных строк'
        : 'Принять все неблокирующие предупреждения выбранных строк';

  const handleExclude = async (exclude: boolean) => {
    setBulkBusy(true);
    try {
      await onBulkExclude(selectedLines, exclude, bulkReason);
      setBulkReason('');
      setSelectedLines(new Set());
    } finally {
      setBulkBusy(false);
    }
  };

  const handleDecision = async (subset: 'safe' | 'errors') => {
    setBulkBusy(true);
    try {
      await onBulkDecision(selectedLines, 'accepted', subset);
      setSelectedLines(new Set());
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <>
      <TableTitle>
        Строки <Badge $tone="neutral">{lines.length}</Badge>
        {ambiguousCount > 0 && (
          <Badge $tone="warning">неоднозначных {ambiguousCount}</Badge>
        )}
        {acts.length > 1 && (
          <FilterSelect
            value={actFilter}
            onChange={(e) => onActFilterChange(e.target.value)}
          >
            <option value="">все акты</option>
            {acts.map((a) => (
              <option key={a.id} value={a.id}>
                акт № {a.act_no}
              </option>
            ))}
          </FilterSelect>
        )}
      </TableTitle>

      {selectedLines.size > 0 && (
        <BulkBar>
          <div>
            <Title>Выбрано строк: {selectedLines.size}</Title>
            <Muted>
              Правки хранятся отдельно от состава и переживают пересборку — в акте они
              появятся после ближайшей сборки.
            </Muted>
          </div>
          <ReasonField>
            <Label>Причина правки</Label>
            <Input
              value={bulkReason}
              onChange={(e) => setBulkReason(e.target.value)}
              placeholder="напр. «объём перенесён в акт 35.2 по письму №14»"
            />
          </ReasonField>
          <Button
            $variant="secondary"
            $size="sm"
            type="button"
            disabled={bulkBusy || !canWrite || frozen}
            title={!canWrite ? 'Только чтение' : undefined}
            onClick={() => handleExclude(true)}
          >
            Исключить из итога
          </Button>
          <Button
            $variant="secondary"
            $size="sm"
            type="button"
            disabled={bulkBusy || !canWrite || frozen}
            title={!canWrite ? 'Только чтение' : undefined}
            onClick={() => handleExclude(false)}
          >
            Вернуть в итог
          </Button>
          <Button
            $variant="ghost"
            $size="sm"
            type="button"
            disabled={bulkBusy || !canWrite || frozen || bulkPending.safe.length === 0}
            title={bulkSafeTitle}
            onClick={() => handleDecision('safe')}
          >
            Принять находки строк ({bulkPending.safe.length})
          </Button>
          {bulkPending.errors.length > 0 && (
            <Button
              $variant="danger"
              $size="sm"
              type="button"
              disabled={bulkBusy || !canWrite || frozen}
              title={
                !canWrite
                  ? 'Только чтение'
                  : frozen
                    ? 'Пакет выпущен — решения по находкам заморожены'
                    : 'Снимает гейт выпуска по этим находкам — решение необратимо'
              }
              onClick={() => handleDecision('errors')}
            >
              Принять ошибки ({bulkPending.errors.length})
            </Button>
          )}
          <Button
            $variant="ghost"
            $size="sm"
            type="button"
            onClick={() => setSelectedLines(new Set())}
          >
            Снять выбор
          </Button>
        </BulkBar>
      )}

      {lines.length === 0 ? (
        <NoLinesMuted>
          Строк нет. Вернитесь на шаг «Сборка» и нажмите «Собрать» — состав
          материализуется из фактов объёмов, журнала ПТО и втянутой первички.
        </NoLinesMuted>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedLines.size === lines.length && lines.length > 0}
                    disabled={frozen}
                    onChange={toggleAllLines}
                    aria-label="Выбрать все строки"
                    title={frozen ? 'Пакет выпущен — состав заморожен' : undefined}
                  />
                </th>
                <th>№</th>
                <th>Шифр</th>
                <th>Наименование</th>
                <th>Ед.</th>
                <ThRight>Объём</ThRight>
                <ThRight>Договор</ThRight>
                <ThRight>Цена ед.</ThRight>
                <ThRight>Стоимость</ThRight>
                <th>Матч</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <LineRow
                  key={l.id}
                  line={l}
                  issues={issuesByLine.get(l.id) ?? []}
                  packageId={packageId}
                  canWrite={canWrite}
                  frozen={frozen}
                  selected={selectedLines.has(l.id)}
                  onToggle={toggleLine}
                  onNotice={onNotice}
                />
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </>
  );
}
